import { Children, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CarouselControlContainerProp, CSSProperties } from 'nuka-carousel';

import { ICarouselProps } from '@r1-frontend/ui-react/components/NukaCarousel';

import { CustomNukaCarousel } from './styled';

export interface ISlideCarouselProps extends ICarouselProps {
    children: Array<ReactNode>,
    slideWidth: number,
    cellSpacing: number,
    dragging?: boolean,
    cursorType?: string,
    customControlsContainerStyles?: (key: CarouselControlContainerProp) => CSSProperties,
}

const DOTS_PADDING = -30;
const ARROWS_PADDING = -22;

const defaultControlsContainerStyles = (key: CarouselControlContainerProp): CSSProperties => {
    switch (key) {
        case 'BottomCenter':
            return {
                bottom: DOTS_PADDING,
            };
        case 'CenterRight':
            return {
                right: `${ARROWS_PADDING}px`,
            };
        case 'CenterLeft':
            return {
                left: `${ARROWS_PADDING}px`,
            };
        default: return {};
    }
};

/**
 * Карусель с автоматическим определением количества видимых слайдов
 * @param slideWidth - ширина слайда карусели
 * @param cellSpacing - расстояние между слайдами
 * @param slideIndex - прокрутка к указанному слайду
 */
const SlideCarousel = ({
    children,
    slideWidth = 304,
    cellSpacing = 20,
    withoutArrows,
    withoutDots,
    dragging = true,
    cursorType = 'initial',
    slideIndex = 0,
    customControlsContainerStyles = defaultControlsContainerStyles,
    ...props
}: ISlideCarouselProps): JSX.Element => {
    const swiperRef = useRef<HTMLInputElement | null>(null);

    const [currIndex, setCurrIndex] = useState(slideIndex);

    const [sliderSettings, setSettings] = useState({
        correctSlideWidth: slideWidth,
        isAllSlidesView: false,
    });

    const onResize = useCallback(() => {
        if (!swiperRef.current) {
            return;
        }

        const oneSlideWidth = slideWidth + cellSpacing;

        const sliderWidth = swiperRef.current.clientWidth;

        const slideCount = sliderWidth / oneSlideWidth;

        const correctSlideWidth = sliderWidth < oneSlideWidth ? sliderWidth - cellSpacing : slideWidth;

        const isAllSlidesView = Children.count(children) < slideCount;

        setSettings({
            correctSlideWidth,
            isAllSlidesView,
        });
    }, [cellSpacing, slideWidth, children]);

    useEffect(() => {
        onResize();
    }, [onResize]);

    useEffect(() => {
        const newIndex = slideIndex === -1 ? 0 : slideIndex;

        if (newIndex !== currIndex) {
            setCurrIndex(newIndex);
        }
    }, [currIndex, slideIndex]);

    return (
        <CustomNukaCarousel
            innerRef={swiperRef}
            slideIndex={sliderSettings.isAllSlidesView ? 0 : currIndex}
            slideWidth={`${sliderSettings.correctSlideWidth}px`}
            cellSpacing={cellSpacing}
            slidesToScroll={'auto'}
            onResize={onResize}
            disableEdgeSwiping={true}
            easing={'easeCubicOut'}
            edgeEasing={'easeCubicOut'}
            withoutArrows={sliderSettings.isAllSlidesView || withoutArrows}
            withoutDots={sliderSettings.isAllSlidesView || withoutDots}
            dragging={!sliderSettings.isAllSlidesView && dragging}
            getControlsContainerStyles={customControlsContainerStyles}
            cursorType={cursorType}
            {...props}
        >
            {Children.map(children, (child) => {
                return child;
            })}
        </CustomNukaCarousel>
    );
};

export default SlideCarousel;
